import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Alert,
  Typography,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  App as AntApp,
  DatePicker,
} from 'antd';
import { PlusOutlined, LinkOutlined, EditOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import apiClient from '@/services/api';
import { useSudoGate } from '@/hooks/useSudoGate';

const { Title } = Typography;

interface OrgRow {
  idx: string;
  name: string;
  enabled: boolean;
  canSelfManageUnits?: boolean;
  created: string | null;
  orgType: 'vendor' | 'distributor';
  vendors?: { vendorIdx: string; vendorName: string; validFrom: string }[];
}

interface OrgResp {
  Result: string;
  vendors: OrgRow[];
  distributors: OrgRow[];
  totals: { vendors: number; distributors: number };
}

export default function AdminHome() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { require: requireSudo, sudoActive, modal: sudoModal } = useSudoGate();
  const [vendorOpen, setVendorOpen] = useState(false);
  const [distOpen, setDistOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<OrgRow | null>(null);
  const [editDist, setEditDist] = useState<OrgRow | null>(null);
  const queryClient = useQueryClient();
  const [vendorForm] = Form.useForm();
  const [distForm] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [editVendorForm] = Form.useForm();
  const [editDistForm] = Form.useForm();

  const orgs = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: async () => {
      const r = await apiClient.get<OrgResp>('/admin/organizations');
      return r as unknown as OrgResp;
    },
  });

  const createVendor = useMutation({
    mutationFn: async (body: any) => apiClient.post('/admin/vendors', body),
    onSuccess: () => {
      message.success('Vendor created');
      setVendorOpen(false);
      vendorForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const createDistributor = useMutation({
    mutationFn: async (body: any) => apiClient.post('/admin/distributors', body),
    onSuccess: () => {
      message.success('Distributor created');
      setDistOpen(false);
      distForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const createLink = useMutation({
    mutationFn: async (body: any) => apiClient.post('/admin/distributor-vendors', body),
    onSuccess: () => {
      message.success('Distributor ↔ Vendor linked');
      setLinkOpen(false);
      linkForm.resetFields();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const terminateVendor = useMutation({
    mutationFn: async (id: string) => apiClient.post(`/admin/vendors/${id}/terminate`, { reason: 'admin_ui' }),
    onSuccess: (data: any) => {
      const term = (data as any)?.terminated;
      message.success(`Terminated: ${term?.plansDisabled ?? 0} plans, ${term?.unitsDisabled ?? 0} units disabled`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const restoreVendor = useMutation({
    mutationFn: async (id: string) => apiClient.post(`/admin/vendors/${id}/restore`),
    onSuccess: (data: any) => {
      const r = (data as any)?.restored;
      message.success(`Restored: ${r?.plansRestored ?? 0} plans re-enabled`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const updateVendor = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) => apiClient.patch(`/admin/vendors/${id}`, body),
    onSuccess: () => { message.success('Vendor updated'); setEditVendor(null); queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  const updateDistributor = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) => apiClient.patch(`/admin/distributors/${id}`, body),
    onSuccess: () => { message.success('Distributor updated'); setEditDist(null); queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] }); },
    onError: (e: any) => message.error(e?.response?.data?.message || String(e)),
  });

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('admin.orgOverview')}
        </Title>
        <Space>
          {sudoActive && <Tag color="orange">SUDO</Tag>}
          <Button
            icon={<PlusOutlined />}
            onClick={() => requireSudo(() => setVendorOpen(true))}
          >
            {t('admin.newVendor')}
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => requireSudo(() => setDistOpen(true))}
          >
            {t('admin.newDistributor')}
          </Button>
          <Button
            icon={<LinkOutlined />}
            onClick={() => requireSudo(() => setLinkOpen(true))}
          >
            {t('admin.linkDV')}
          </Button>
        </Space>
      </Space>

      {orgs.error ? (
        <Alert type="error" message="Failed to load organizations" description={String(orgs.error)} />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic title={t('admin.vendors')} value={orgs.data?.totals?.vendors ?? 0} loading={orgs.isLoading} />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={t('admin.distributors')}
                  value={orgs.data?.totals?.distributors ?? 0}
                  loading={orgs.isLoading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={t('admin.selfManage')}
                  value={orgs.data?.vendors?.filter((v) => v.canSelfManageUnits).length ?? 0}
                  loading={orgs.isLoading}
                />
              </Card>
            </Col>
          </Row>

          <Card title={t('admin.vendors')} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="idx"
              loading={orgs.isLoading}
              dataSource={orgs.data?.vendors ?? []}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: t('common.name'), dataIndex: 'name', key: 'name' },
                {
                  title: t('common.enabled'),
                  dataIndex: 'enabled',
                  key: 'enabled',
                  width: 100,
                  render: (v: boolean) => (v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag>),
                },
                {
                  title: t('admin.selfManage'),
                  dataIndex: 'canSelfManageUnits',
                  key: 'sm',
                  width: 120,
                  render: (v: boolean) =>
                    v ? <Tag color="blue">{t('admin.authorized')}</Tag> : <Tag>—</Tag>,
                },
                {
                  title: t('common.uuid'),
                  dataIndex: 'idx',
                  key: 'idx',
                  render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>,
                },
                {
                  title: t('common.actions'),
                  key: 'vact',
                  width: 260,
                  render: (_: any, rec: OrgRow) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />} onClick={() => {
                        setEditVendor(rec);
                        editVendorForm.setFieldsValue({ name: rec.name, enabled: rec.enabled, canSelfManageUnits: rec.canSelfManageUnits });
                      }}>{t('admin.edit')}</Button>
                      {rec.enabled ? (
                        <Popconfirm title={t('admin.terminateConfirm')} description={t('admin.terminateDesc')}
                          onConfirm={() => requireSudo(() => terminateVendor.mutate(rec.idx))}>
                          <Button size="small" danger>{t('admin.terminate')}</Button>
                        </Popconfirm>
                      ) : (
                        <Popconfirm title={t('admin.restoreConfirm')} description={t('admin.restoreDesc')}
                          onConfirm={() => requireSudo(() => restoreVendor.mutate(rec.idx))}>
                          <Button size="small" type="primary">{t('admin.restore')}</Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Card title={t('admin.distributors')}>
            <Table
              size="small"
              rowKey="idx"
              loading={orgs.isLoading}
              dataSource={orgs.data?.distributors ?? []}
              pagination={{ pageSize: 20 }}
              columns={[
                { title: t('common.name'), dataIndex: 'name', key: 'name' },
                {
                  title: 'Vendors',
                  key: 'vendors',
                  render: (_: any, rec: OrgRow) => {
                    const vlist = (rec as any).vendors ?? [];
                    if (vlist.length === 0) return <span style={{ color: '#aaa' }}>—</span>;
                    return <Space size={4} wrap>{vlist.map((v: any) => <Tag key={v.vendorIdx}>{v.vendorName}</Tag>)}</Space>;
                  },
                },
                {
                  title: t('common.enabled'),
                  dataIndex: 'enabled',
                  key: 'enabled',
                  width: 100,
                  render: (v: boolean) => (v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag>),
                },
                {
                  title: t('common.uuid'),
                  dataIndex: 'idx',
                  key: 'idx',
                  render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>,
                },
                {
                  title: t('common.actions'),
                  key: 'dact',
                  width: 200,
                  render: (_: any, rec: OrgRow) => (
                    <Space size="small">
                      <Button size="small" icon={<EditOutlined />} onClick={() => {
                        setEditDist(rec);
                        editDistForm.setFieldsValue({ name: rec.name, enabled: rec.enabled });
                      }}>{t('admin.edit')}</Button>
                      {rec.enabled ? (
                        <Popconfirm title={t('admin.disable') + '?'}
                          onConfirm={() => requireSudo(() => updateDistributor.mutate({ id: rec.idx, body: { enabled: false } }))}>
                          <Button size="small" danger>{t('admin.disable')}</Button>
                        </Popconfirm>
                      ) : (
                        <Popconfirm title={t('admin.enable') + '?'}
                          onConfirm={() => updateDistributor.mutate({ id: rec.idx, body: { enabled: true } })}>
                          <Button size="small" type="primary">{t('admin.enable')}</Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      <Modal
        title={t('admin.newVendor')}
        open={vendorOpen}
        onCancel={() => setVendorOpen(false)}
        onOk={() => vendorForm.submit()}
        confirmLoading={createVendor.isPending}
        okText="Create"
      >
        <Form form={vendorForm} layout="vertical" onFinish={(v) => createVendor.mutate(v)}>
          <Form.Item name="name" label="Vendor name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="canSelfManageUnits" label="Authorize self-manage units?" initialValue={false}>
            <Select
              options={[
                { label: 'No', value: false },
                { label: 'Yes', value: true },
              ]}
            />
          </Form.Item>
          <Form.Item name="comment" label="Comment">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('admin.newDistributor')}
        open={distOpen}
        onCancel={() => setDistOpen(false)}
        onOk={() => distForm.submit()}
        confirmLoading={createDistributor.isPending}
        okText="Create"
      >
        <Form form={distForm} layout="vertical" onFinish={(v) => createDistributor.mutate(v)}>
          <Form.Item name="name" label="Distributor name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="comment" label="Comment">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('admin.linkDV')}
        open={linkOpen}
        onCancel={() => setLinkOpen(false)}
        onOk={() => linkForm.submit()}
        confirmLoading={createLink.isPending}
        okText="Link"
      >
        <Form
          form={linkForm}
          layout="vertical"
          onFinish={(v) => {
            const payload: any = {
              distributorIdx: v.distributorIdx,
              vendorIdx: v.vendorIdx,
            };
            if (v.validFrom) payload.validFrom = (v.validFrom as Dayjs).toISOString();
            if (v.commissionRate) payload.commissionRate = Number(v.commissionRate);
            createLink.mutate(payload);
          }}
        >
          <Form.Item name="distributorIdx" label="Distributor" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={orgs.data?.distributors?.map((d) => ({ label: d.name, value: d.idx })) ?? []}
            />
          </Form.Item>
          <Form.Item name="vendorIdx" label="Vendor" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={orgs.data?.vendors?.map((v) => ({ label: v.name, value: v.idx })) ?? []}
            />
          </Form.Item>
          <Form.Item name="validFrom" label="Valid from (default: now)">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="commissionRate" label="Commission rate (optional)">
            <Input type="number" step="0.0001" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={t('admin.editVendor')} open={!!editVendor} onCancel={() => setEditVendor(null)}
        onOk={() => editVendorForm.submit()} confirmLoading={updateVendor.isPending} okText="Save">
        <Form form={editVendorForm} layout="vertical" onFinish={(v) => {
          if (!editVendor) return;
          requireSudo(() => updateVendor.mutate({ id: editVendor.idx, body: v }));
        }}>
          <Form.Item name="name" label="Name"><Input /></Form.Item>
          <Form.Item name="enabled" label="Enabled">
            <Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />
          </Form.Item>
          <Form.Item name="canSelfManageUnits" label="Self-manage units">
            <Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={t('admin.editDistributor')} open={!!editDist} onCancel={() => setEditDist(null)}
        onOk={() => editDistForm.submit()} confirmLoading={updateDistributor.isPending} okText="Save">
        <Form form={editDistForm} layout="vertical" onFinish={(v) => {
          if (!editDist) return;
          updateDistributor.mutate({ id: editDist.idx, body: v });
        }}>
          <Form.Item name="name" label="Name"><Input /></Form.Item>
          <Form.Item name="enabled" label="Enabled">
            <Select options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]} />
          </Form.Item>
        </Form>
      </Modal>

      {sudoModal}
    </>
  );
}
